import {useFormContext} from 'react-hook-form';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@common-web/components/ui/form';
import {Input} from '@common-web/components/ui/input';
import {Button} from '@common-web/components/ui/button';
import {useState} from 'react';
import {User, Phone, Mail, Briefcase, Copy} from 'lucide-react';

type ContactFieldName = 'firstName' | 'lastName' | 'title' | 'phone' | 'email';

export function ContactFields({
  basePath,
  titlePrefix,
  copySources,
  requiredFields,
}: {
  basePath: string;
  titlePrefix: string;
  copySources?: {label: string; path: string}[];
  /** Fields to mark as required (asterisk + aria-required). Validation lives in rfcSchema. */
  requiredFields?: ContactFieldName[];
}) {
  const {control, getValues, setValue} = useFormContext();
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const requiredMark = (field: ContactFieldName) => (requiredFields?.includes(field) ? ' *' : '');
  const ariaRequired = (field: ContactFieldName) => requiredFields?.includes(field) || undefined;

  const handleCopyFrom = (path: string) => {
    const src = getValues(path);
    if (!src) return;
    setValue(basePath, src, {shouldDirty: true, shouldValidate: true});
    setCopyMenuOpen(false);
  };

  return (
    <div className='border rounded-lg p-4 space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='font-medium'>{titlePrefix}</div>
        {!!copySources && copySources.length > 0 && (
          <div className='relative'>
            <Button
              type='button'
              variant='link'
              size='sm'
              onClick={() => setCopyMenuOpen((v) => !v)}
            >
              <Copy className='mr-2 h-4 w-4' /> Copy from other contact
            </Button>
            {copyMenuOpen && (
              <div className='absolute right-0 mt-2 w-56 rounded-md border bg-popover shadow-md z-50'>
                <div className='py-1'>
                  {copySources.map((src) => (
                    <button
                      key={src.path}
                      type='button'
                      className='w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground'
                      onClick={() => handleCopyFrom(src.path)}
                    >
                      {src.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <FormField
          control={control}
          name={`${basePath}.firstName`}
          render={({field}) => (
            <FormItem>
              <FormLabel>First Name{requiredMark('firstName')}</FormLabel>
              <FormControl>
                <div className='relative'>
                  <User className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                  <Input
                    {...field}
                    className='pl-9'
                    placeholder='First name'
                    autoComplete='given-name'
                    aria-required={ariaRequired('firstName')}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`${basePath}.lastName`}
          render={({field}) => (
            <FormItem>
              <FormLabel>Last Name{requiredMark('lastName')}</FormLabel>
              <FormControl>
                <div className='relative'>
                  <User className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                  <Input
                    {...field}
                    className='pl-9'
                    placeholder='Last name'
                    autoComplete='family-name'
                    aria-required={ariaRequired('lastName')}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`${basePath}.title`}
          render={({field}) => (
            <FormItem>
              <FormLabel>Title{requiredMark('title')}</FormLabel>
              <FormControl>
                <div className='relative'>
                  <Briefcase className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                  <Input
                    {...field}
                    className='pl-9'
                    placeholder='Job title'
                    aria-required={ariaRequired('title')}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`${basePath}.phone`}
          render={({field}) => (
            <FormItem>
              <FormLabel>Phone{requiredMark('phone')}</FormLabel>
              <FormControl>
                <div className='relative'>
                  <Phone className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                  <Input
                    {...field}
                    className='pl-9'
                    type='tel'
                    placeholder='555-555-5555'
                    autoComplete='tel'
                    aria-required={ariaRequired('phone')}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name={`${basePath}.email`}
          render={({field}) => (
            <FormItem>
              <FormLabel>Email{requiredMark('email')}</FormLabel>
              <FormControl>
                <div className='relative'>
                  <Mail className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                  <Input
                    type='email'
                    {...field}
                    className='pl-9'
                    placeholder='name@example.com'
                    autoComplete='email'
                    aria-required={ariaRequired('email')}
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
